import type { TenantSmtpConnectionFragment } from "~/gql/queries/tenantSmtpConnection.generated";
import type { SenderSetupView } from "~/helpers/aiChat/fernieSenderSetup";

import { useContext, useMemo } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import { getCompanySettingsAccess } from "~/auth/roleChecks";
import useMyUser from "~/auth/useMyUser";
import fernieSenderSetup from "~/helpers/aiChat/fernieSenderSetup";
import {
	useGetTenantEmailDomainQuery,
	useGetTenantEmailSenderSelectionQuery,
	useGetTenantEmailSendersQuery,
	useGetTenantSmtpConnectionQuery
} from "~/services/endpoints/apiEndpoints";

export interface UseFernieSenderSetupReturn {
	isError: boolean;
	refetch: () => void;
	smtp: TenantSmtpConnectionFragment | null;
	view: SenderSetupView | null;
}

const useFernieSenderSetup = (isActive: boolean): UseFernieSenderSetupReturn => {
	const { currentUser } = useContext(AuthContext);
	const { myUser } = useMyUser();
	const isShown = isActive && getCompanySettingsAccess(currentUser, myUser?.roles).canManageEmail;
	const options = { skip: !isShown };
	const selection = useGetTenantEmailSenderSelectionQuery(undefined, options);
	const senders = useGetTenantEmailSendersQuery({ first: 50 }, options);
	const domain = useGetTenantEmailDomainQuery(undefined, options);
	const smtp = useGetTenantSmtpConnectionQuery(undefined, options);
	const queries = [selection, senders, domain, smtp];

	const view = useMemo(
		() =>
			selection.currentData && senders.currentData && domain.currentData && smtp.currentData
				? fernieSenderSetup.buildSenderSetupView({
						domain: domain.currentData.tenantEmailDomain ?? null,
						selection: selection.currentData.tenantEmailSenderSelection,
						senders: senders.currentData.tenantEmailSenders?.nodes ?? [],
						smtp: smtp.currentData.tenantSmtpConnection ?? null
					})
				: null,
		[domain.currentData, selection.currentData, senders.currentData, smtp.currentData]
	);

	const refetch = () => {
		for (const query of queries) query.refetch();
	};

	return {
		isError: queries.some((query) => query.isError),
		refetch,
		smtp: smtp.currentData?.tenantSmtpConnection ?? null,
		view
	};
};

export default useFernieSenderSetup;
